import React, { useMemo, useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Dimensions,
  TouchableOpacity,
  Platform,
  StatusBar,
  Alert,
  TextInput,
  ScrollView,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import { useGoals } from '../context/GoalsContext';
import { cardEdgeStyle } from '../theme/cardEdge';
import { createThemedStyles } from '../theme/useThemedStyles';
import { useTheme } from '../context/ThemeContext';
import type { AppColors } from '../theme/palettes';
import { getGoalTypeProgressColor } from '../theme/goalTypeColors';
import { useNavigation } from '@react-navigation/native';
import type { StackNavigationProp } from '@react-navigation/stack';
import { EventEmitter } from 'fbemitter';
import { useSettings } from '../context/SettingsContext';
import { formatCurrency } from '../utils/currency';
import { formatDate } from '../utils/date';
import { useAuth } from '../context/AuthContext';
import { useSubscription } from '../context/SubscriptionContext';
import AdBanner from '../components/AdBanner';
import GoalLimitModal from '../components/GoalLimitModal';
import { Goal } from '../context/GoalsContext';
import { useInterstitialAd } from '../hooks/useInterstitialAd';
import { isWeb } from '../utils/platformUtils';
import WebPage from '../components/web/WebPage';
import BrandWordmark from '../components/BrandWordmark';

// Declare global EventEmitter
declare global {
  var EventEmitter: EventEmitter | undefined;
}

const { width } = Dimensions.get('window');

const buildTypeColorMap = (c: AppColors) => ({
  Savings: {
    bg: c.surface,
    progress: c.dashboard.savingsProgress,
    text: c.text.primary,
  },
  Debt: {
    bg: c.surface,
    progress: c.dashboard.debtProgress,
    text: c.dashboard.debtProgress,
  },
  Investment: {
    bg: c.surface,
    progress: c.grey[800],
    text: c.text.primary,
  },
});

const DashboardScreen = () => {
  const { colors: themeColors, resolvedMode } = useTheme();
  const insets = useSafeAreaInsets();
  const typeColorMap = useMemo(() => buildTypeColorMap(themeColors), [themeColors]);
  const { goals, income } = useGoals();
  const { settings } = useSettings();
  const { user } = useAuth();
  const navigation = useNavigation<StackNavigationProp<any>>();
  const { subscription, canShowDashboardCard, currentTier } = useSubscription();
  const [goalLimitModalVisible, setGoalLimitModalVisible] = useState(false);

  // Show interstitial ad when screen comes into focus (rate limited to every 10 minutes)
  useInterstitialAd();
  // Search and filter state for dashboard
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTypeFilter, setSelectedTypeFilter] = useState<Goal['type'] | 'all'>('all');
  const [selectedPriorityFilter, setSelectedPriorityFilter] = useState<'Essential' | 'Important' | 'Optional' | 'all'>('all');
  const [showFilters, setShowFilters] = useState(false);
  const styles = useDashboardStyles();

  const priorityColorMap = {
    Essential: themeColors.error.main,
    Important: themeColors.warning.main,
    Optional: themeColors.success.main,
  } as const;

  // Debug logging for subscription and dashboard cards
  useEffect(() => {
    console.log('Dashboard Debug:', {
      currentTier,
      subscription: subscription?.tier,
      subscriptionStatus: subscription?.status,
      canShowInvestmentCard: canShowDashboardCard('investmentOverview'),
      investmentGoalsCount: goals.filter(g => g.type === 'Investment').length
    });
  }, [currentTier, subscription, canShowDashboardCard, goals]);

  const calculateDebtWithInterest = (currentAmount: number, targetAmount: number, monthlyContribution: number, apr?: number) => {
    if (!apr) return { total: currentAmount, target: targetAmount };

    const monthlyRate = apr / 100 / 12;
    let totalPaid = currentAmount;
    let remainingDebt = targetAmount - currentAmount;
    let monthsToComplete = 0;
    
    // Calculate total amount including future interest
    while (remainingDebt > 0 && monthsToComplete < 600) { // Cap at 50 years
      // Add monthly interest
      const monthlyInterest = remainingDebt * monthlyRate;
      remainingDebt += monthlyInterest;
      
      // Subtract monthly payment
      const payment = Math.min(monthlyContribution, remainingDebt);
      remainingDebt -= payment;
      totalPaid += payment;
      
      monthsToComplete++;
    }

    return {
      total: totalPaid,
      target: totalPaid + remainingDebt // Total amount needed including all interest
    };
  };

  const stats = useMemo(() => {
    const totalSaved = goals.reduce((sum, goal) => sum + goal.currentAmount, 0);
    const totalTarget = goals.reduce((sum, goal) => sum + goal.targetAmount, 0);
    const overallProgress = totalTarget > 0 ? (totalSaved / totalTarget) * 100 : 0;
    const activeGoals = goals.length;
    const allocatedIncome = goals.reduce((sum, goal) => sum + goal.monthlyContribution, 0);
    const allocatedPercentage = income.monthlyIncome > 0 ? (allocatedIncome / income.monthlyIncome) * 100 : 0;

    // Calculate type-specific totals and deadlines
    const savingsGoals = goals.filter(goal => goal.type === 'Savings');
    const debtGoals = goals.filter(goal => goal.type === 'Debt');
    const investmentGoals = goals.filter(goal => goal.type === 'Investment');

    const calculateTypeStats = (typeGoals: Goal[]) => {
      if (typeGoals.length === 0) return { 
        total: 0, 
        target: 0, 
        progress: 0, 
        count: 0, 
        furthestDate: null, 
        totalInterest: 0, 
        totalInterestPaid: 0 
      };
      
      const total = typeGoals.reduce((sum, goal) => sum + goal.currentAmount, 0);
      const target = typeGoals.reduce((sum, goal) => sum + goal.targetAmount, 0);
      const progress = target > 0 ? (total / target) * 100 : 0;
      const count = typeGoals.length;
      
      // Find the furthest deadline
      const furthestDate = typeGoals
        .filter(goal => goal.deadline)
        .sort((a, b) => new Date(b.deadline!).getTime() - new Date(a.deadline!).getTime())[0]?.deadline || null;
      
      // Calculate total interest for debt goals
      const totalInterest = typeGoals.reduce((sum, goal) => {
        if (goal.type === 'Debt' && goal.apr && goal.originalTargetAmount) {
          const remainingBalance = goal.originalTargetAmount - goal.currentAmount;
          const annualInterest = remainingBalance * (goal.apr / 100);
          return sum + annualInterest;
        }
        return sum;
      }, 0);
      
      // Calculate total interest paid
      const totalInterestPaid = typeGoals.reduce((sum, goal) => {
        if (goal.type === 'Debt' && goal.payments) {
          return sum + goal.payments.reduce((paymentSum, payment) => paymentSum + (payment.amount || 0), 0);
        }
        return sum;
      }, 0);
      
      return { total, target, progress, count, furthestDate, totalInterest, totalInterestPaid };
    };

    return {
      totalSaved,
      overallProgress,
      activeGoals,
      allocatedIncome,
      allocatedPercentage,
      savings: calculateTypeStats(savingsGoals),
      debt: calculateTypeStats(debtGoals),
      investment: calculateTypeStats(investmentGoals),
    };
  }, [goals, income]);

  const getTimeOfDay = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  const getFirstName = () => {
    // Read from SettingsContext first, then fall back to AuthContext
    const displayName = settings?.profile?.displayName || user?.displayName;
    if (!displayName) return '';
    return displayName.split(' ')[0];
  };

  const renderProgressBar = (
    currentAmount: number,
    targetAmount: number,
    goalType: Goal['type']
  ) => {
    const progress = (currentAmount / targetAmount) * 100;
    return (
      <View style={styles.progressBarContainer}>
        <View
          style={[
            styles.progressBar,
            {
              width: `${progress}%`,
              backgroundColor: getGoalTypeProgressColor(goalType, themeColors),
            },
          ]}
        />
      </View>
    );
  };

  // Function to check if user can create a new goal based on subscription
  const canCreateGoal = (currentGoalCount: number) => {
    if (!subscription) return currentGoalCount < 3; // Free tier: 3 goals
    if (subscription.tier === 'standard') return currentGoalCount < 10; // Standard tier: 10 goals
    return true; // Premium tier: unlimited goals
  };

  // Custom handler for adding a goal with subscription limit check
  const handleAddGoalWithLimitCheck = () => {
    if (!canCreateGoal(goals.length)) {
      setGoalLimitModalVisible(true);
      return;
    }
    // Proceed with navigation to Goals screen
    navigation.navigate('Goals', { openAddGoalModal: true });
  };

  // Filtering logic for dashboard goals
  const filteredGoals = useMemo(() => {
    return goals.filter(goal => {
      // Search query filter
      const matchesSearch = searchQuery === '' || goal.name.toLowerCase().includes(searchQuery.toLowerCase());
      // Goal type filter
      const matchesType = selectedTypeFilter === 'all' || goal.type === selectedTypeFilter;
      // Priority filter
      const matchesPriority = selectedPriorityFilter === 'all' || goal.priority === selectedPriorityFilter;
      return matchesSearch && matchesType && matchesPriority;
    });
  }, [goals, searchQuery, selectedTypeFilter, selectedPriorityFilter]);

  const quickActionsBar = (
    <View style={styles.quickActions}>
      <TouchableOpacity
        style={styles.actionButton}
        onPress={handleAddGoalWithLimitCheck}
      >
        <Ionicons name="add-circle-outline" size={24} color={themeColors.primary.main} />
        <Text style={styles.actionText}>New Goal</Text>
      </TouchableOpacity>
      <TouchableOpacity
        style={styles.actionButton}
        onPress={() => navigation.navigate('Income')}
      >
        <Ionicons name="wallet-outline" size={24} color={themeColors.primary.main} />
        <Text style={styles.actionText}>Update Income</Text>
      </TouchableOpacity>
      <TouchableOpacity
        style={styles.actionButton}
        onPress={() => navigation.navigate('Analytics')}
      >
        <Ionicons name="analytics-outline" size={24} color={themeColors.primary.main} />
        <Text style={styles.actionText}>Analytics</Text>
      </TouchableOpacity>
    </View>
  );

  const dashboardScrollBody = (
    <>
      {/* Header — brand lives in left nav on web */}
      {!isWeb && (
        <View
          style={[
            styles.header,
            {
              backgroundColor: themeColors.primary.header,
              paddingTop: insets.top + 12,
            },
          ]}
        >
          <StatusBar barStyle="light-content" backgroundColor={themeColors.primary.header} translucent={false} />
          <View style={styles.brandContainer}>
            <BrandWordmark variant="light" size="md" />
            <Text style={styles.brandTagline}>Smart Financial Goals</Text>
          </View>
          <TouchableOpacity
            style={styles.settingsButton}
            onPress={() => navigation.navigate('Settings')}
          >
            <Ionicons name="settings-outline" size={24} color="white" />
          </TouchableOpacity>
        </View>
      )}

      {/* Welcome Section — web uses WebPage header card */}
      {!isWeb && (
      <View
        style={[
          styles.welcomeSection,
          { backgroundColor: themeColors.background.paper, borderBottomColor: themeColors.border },
        ]}
      >
        <Text style={[styles.greeting, { color: themeColors.text.primary }]}>
          {getTimeOfDay()}
          {getFirstName() ? `, ${getFirstName()}` : ''}
        </Text>
        <Text style={[styles.subtitle, { color: themeColors.text.secondary }]}>
          Here's your financial overview
        </Text>
      </View>
      )}

      {/* Summary Cards */}
      <View style={[styles.summaryContainer, isWeb && styles.summaryContainerWeb]}>
        {canShowDashboardCard('totalGoals') && (
          <View
            style={[
              styles.summaryCard,
              { backgroundColor: themeColors.dashboard.incomeCard },
              cardEdgeStyle(themeColors),
            ]}
          >
            <Text style={styles.summaryLabel}>Monthly Income</Text>
            <Text style={[styles.summaryValue, { color: themeColors.accent.mint }]}>
              {formatCurrency(income.monthlyIncome, settings.currency.code, settings.currency.symbol)}
            </Text>
            <Text style={styles.summarySubtext}>
              {stats.allocatedPercentage.toFixed(2)}% Allocated
            </Text>
          </View>
        )}

        {canShowDashboardCard('totalProgress') && (
          <View
            style={[
              styles.summaryCard,
              { backgroundColor: themeColors.dashboard.savedPaidCard },
              cardEdgeStyle(themeColors),
            ]}
          >
            <Text style={styles.summaryLabel}>Total Saved/Paid</Text>
            <Text style={styles.summaryValue}>
              {formatCurrency(stats.totalSaved, settings.currency.code, settings.currency.symbol)}
            </Text>
            <Text style={styles.summarySubtext}>
              {stats.overallProgress.toFixed(2)}% of Goals
            </Text>
          </View>
        )}
      </View>

      {/* Type Summary Cards */}
      <View style={styles.typeSummaryContainer}>
        {canShowDashboardCard('savingsOverview') && (
          <View style={[styles.typeSummaryCard, { backgroundColor: typeColorMap.Savings.bg }]}>
            <View style={styles.typeHeader}>
              <View style={styles.typeIconContainer}>
                <Text style={styles.typeIcon}>💰</Text>
                <Text style={styles.typeLabel}>Total Savings</Text>
              </View>
              <View style={styles.typeMetaContainer}>
                <Text style={styles.typeGoalCount}>{stats.savings.count} Goals</Text>
                {stats.savings.furthestDate && (
                  <Text style={styles.typeDeadline}>
                    Due {formatDate(stats.savings.furthestDate, settings.dateFormat)}
                  </Text>
                )}
              </View>
            </View>
            <Text style={[styles.typeAmount, { color: typeColorMap.Savings.text }]}>
              {formatCurrency(stats.savings.total, settings.currency.code, settings.currency.symbol)}
            </Text>
            <View style={styles.typeProgressBar}>
              <View 
                style={[
                  styles.typeProgressFill, 
                  { 
                    width: `${Math.min(stats.savings.progress, 100)}%`,
                    backgroundColor: typeColorMap.Savings.progress,
                  }
                ]} 
              />
              <Text style={[styles.progressText, { color: typeColorMap.Savings.progress }]}>
                {stats.savings.progress.toFixed(2)}%
              </Text>
            </View>
            <View style={styles.typeProgressDetails}>
              <Text style={styles.typeBalance}>
                Balance: {formatCurrency(stats.savings.target - stats.savings.total, settings.currency.code, settings.currency.symbol)}
              </Text>
            </View>
          </View>
        )}

        {canShowDashboardCard('debtOverview') && (
          <View style={[styles.typeSummaryCard, { backgroundColor: typeColorMap.Debt.bg }]}>
            <View style={styles.typeHeader}>
              <View style={styles.typeIconContainer}>
                <Text style={styles.typeIcon}>💳</Text>
                <Text style={styles.typeLabel}>Total Debt Paid</Text>
              </View>
              <View style={styles.typeMetaContainer}>
                <Text style={styles.typeGoalCount}>{stats.debt.count} Goals</Text>
                {stats.debt.furthestDate && (
                  <Text style={styles.typeDeadline}>
                    Due {formatDate(stats.debt.furthestDate, settings.dateFormat)}
                  </Text>
                )}
              </View>
            </View>
            <Text style={[styles.typeAmount, { color: typeColorMap.Debt.text }]}>
              {formatCurrency(stats.debt.total, settings.currency.code, settings.currency.symbol)}
            </Text>
            <View style={styles.typeProgressBar}>
              <View 
                style={[
                  styles.typeProgressFill, 
                  { 
                    width: `${Math.min(stats.debt.progress, 100)}%`,
                    backgroundColor: typeColorMap.Debt.progress,
                  }
                ]} 
              />
              <Text style={[styles.progressText, { color: typeColorMap.Debt.progress }]}>
                {stats.debt.progress.toFixed(2)}%
              </Text>
            </View>
            <View style={styles.typeProgressDetails}>
              <Text style={styles.typeBalance}>
                Balance: {formatCurrency(stats.debt.target - stats.debt.total, settings.currency.code, settings.currency.symbol)}
              </Text>
              {stats.debt.totalInterest > 0 && (
                <>
                  <Text style={styles.typeInterest}>
                    Interest: {formatCurrency(stats.debt.totalInterest, settings.currency.code, settings.currency.symbol)}
                  </Text>
                  {stats.debt.totalInterestPaid > 0 && (
                    <Text style={styles.interestPaidText}>
                      Interest Paid: {formatCurrency(stats.debt.totalInterestPaid, settings.currency.code, settings.currency.symbol)}
                    </Text>
                  )}
                </>
              )}
            </View>
          </View>
        )}

        {canShowDashboardCard('investmentOverview') && (
          <View style={[styles.typeSummaryCard, { backgroundColor: typeColorMap.Investment.bg }]}>
            <View style={styles.typeHeader}>
              <View style={styles.typeIconContainer}>
                <Text style={styles.typeIcon}>📈</Text>
                <Text style={styles.typeLabel}>Total Investment</Text>
              </View>
              <View style={styles.typeMetaContainer}>
                <Text style={styles.typeGoalCount}>{stats.investment.count} Goals</Text>
                {stats.investment.furthestDate && (
                  <Text style={styles.typeDeadline}>
                    Due {formatDate(stats.investment.furthestDate, settings.dateFormat)}
                  </Text>
                )}
              </View>
            </View>
            <Text style={[styles.typeAmount, { color: typeColorMap.Investment.text }]}>
              {formatCurrency(stats.investment.total, settings.currency.code, settings.currency.symbol)}
            </Text>
            <View style={styles.typeProgressBar}>
              <View 
                style={[
                  styles.typeProgressFill, 
                  { 
                    width: `${Math.min(stats.investment.progress, 100)}%`,
                    backgroundColor: typeColorMap.Investment.progress,
                  }
                ]} 
              />
              <Text style={[styles.progressText, { color: typeColorMap.Investment.progress }]}>
                {stats.investment.progress.toFixed(2)}%
              </Text>
            </View>
            <View style={styles.typeProgressDetails}>
              <Text style={styles.typeBalance}>
                Balance: {formatCurrency(stats.investment.target - stats.investment.total, settings.currency.code, settings.currency.symbol)}
              </Text>
            </View>
          </View>
        )}
      </View>

      {/* Reduce gap below summary cards */}
      <View style={{ height: 4 }} />

      {/* Search and Filter Bar for standard/premium only */}
      {(currentTier === 'standard' || currentTier === 'premium') && (
        <View style={styles.dashboardSearchFilterContainer}>
          {/* Search Bar */}
          <View style={styles.dashboardSearchContainer}>
            <Ionicons name="search" size={20} color={themeColors.grey[500]} style={styles.dashboardSearchIcon} />
            <TextInput
              style={styles.dashboardSearchInput}
              placeholder="Search goals..."
              value={searchQuery}
              onChangeText={setSearchQuery}
              placeholderTextColor={themeColors.text.placeholder}
            />
            {searchQuery.length > 0 && (
              <TouchableOpacity onPress={() => setSearchQuery('')} style={styles.dashboardClearButton}>
                <Ionicons name="close-circle" size={18} color={themeColors.grey[500]} />
              </TouchableOpacity>
            )}
          </View>
          {/* Filter Toggle Button */}
          <TouchableOpacity
            style={styles.dashboardFilterToggleButton}
            onPress={() => setShowFilters(!showFilters)}
          >
            <Ionicons name="filter" size={18} color={themeColors.primary.main} />
            <Text style={styles.dashboardFilterToggleText}>Filters</Text>
            <Ionicons 
              name={showFilters ? "chevron-up" : "chevron-down"} 
              size={12} 
              color={themeColors.primary.main} 
            />
          </TouchableOpacity>
        </View>
      )}
      {/* Filter Options (smaller) */}
      {(currentTier === 'standard' || currentTier === 'premium') && showFilters && (
        <View style={styles.dashboardFilterOptionsContainer}>
          {/* Goal Type Filter */}
          <View style={styles.dashboardFilterSection}>
            <Text style={styles.dashboardFilterSectionTitle}>Goal Type</Text>
            <View style={styles.dashboardFilterChipsContainer}>
              <TouchableOpacity
                style={[
                  styles.dashboardFilterChip,
                  selectedTypeFilter === 'all' && styles.dashboardFilterChipSelected
                ]}
                onPress={() => setSelectedTypeFilter('all')}
              >
                <Text style={[
                  styles.dashboardFilterChipText,
                  selectedTypeFilter === 'all' && styles.dashboardFilterChipTextSelected
                ]}>All</Text>
              </TouchableOpacity>
              {['Savings','Debt','Investment'].map(type => (
                <TouchableOpacity
                  key={type}
                  style={[
                    styles.dashboardFilterChip,
                    selectedTypeFilter === type && styles.dashboardFilterChipSelected
                  ]}
                  onPress={() => setSelectedTypeFilter(type as Goal['type'])}
                >
                  <Text style={styles.dashboardFilterChipIcon}>{type === 'Savings' ? '💰' : type === 'Debt' ? '💳' : '📈'}</Text>
                  <Text style={[
                    styles.dashboardFilterChipText,
                    selectedTypeFilter === type && styles.dashboardFilterChipTextSelected
                  ]}>{type}</Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>
          {/* Priority Filter */}
          <View style={styles.dashboardFilterSection}>
            <Text style={styles.dashboardFilterSectionTitle}>Priority</Text>
            <View style={styles.dashboardFilterChipsContainer}>
              <TouchableOpacity
                style={[
                  styles.dashboardFilterChip,
                  selectedPriorityFilter === 'all' && styles.dashboardFilterChipSelected
                ]}
                onPress={() => setSelectedPriorityFilter('all')}
              >
                <Text style={[
                  styles.dashboardFilterChipText,
                  selectedPriorityFilter === 'all' && styles.dashboardFilterChipTextSelected
                ]}>All</Text>
              </TouchableOpacity>
              {['Essential','Important','Optional'].map(priority => (
                <TouchableOpacity
                  key={priority}
                  style={[
                    styles.dashboardFilterChip,
                    selectedPriorityFilter === priority && styles.dashboardFilterChipSelected
                  ]}
                  onPress={() => setSelectedPriorityFilter(priority as any)}
                >
                  <Text style={[
                    styles.dashboardFilterChipText,
                    selectedPriorityFilter === priority && styles.dashboardFilterChipTextSelected
                  ]}>{priority}</Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>
          {/* Clear Filters Button */}
          <TouchableOpacity
            style={styles.dashboardClearFiltersButton}
            onPress={() => {
              setSearchQuery('');
              setSelectedTypeFilter('all');
              setSelectedPriorityFilter('all');
            }}
          >
            <Text style={styles.dashboardClearFiltersButtonText}>Clear All Filters</Text>
          </TouchableOpacity>
        </View>
      )}
      {/* Reduce gap below search/filter bar */}
      <View style={{ height: 4 }} />
      {/* Active Goals Section (filtered) */}
      <View style={styles.section}>
        <View style={styles.sectionHeader}>
          <Text style={styles.sectionTitle}>Active Goals</Text>
          <Text style={styles.sectionSubtitle}>{filteredGoals.length} goals</Text>
        </View>
        {filteredGoals.map((goal) => {
          const progress = (goal.currentAmount / goal.targetAmount) * 100;
          const remainingBalance = goal.targetAmount - goal.currentAmount;
          return (
            <View key={goal.id} style={styles.goalCard}>
              <View style={styles.goalHeader}>
                <View style={styles.goalTitleContainer}>
                  <Text style={styles.goalIcon}>
                    {goal.type === 'Savings' ? '💰' : goal.type === 'Debt' ? '💳' : '📈'}
                  </Text>
                  <Text style={styles.goalName}>{goal.name}</Text>
                </View>
                <View style={styles.goalMetaContainer}>
                  <View style={[styles.priorityBadge, { backgroundColor: priorityColorMap[goal.priority] }]}>
                    <Text style={styles.priorityText}>{goal.priority}</Text>
                  </View>
                  <Text style={styles.typeDeadline}>
                    Due {formatDate(goal.deadline, settings.dateFormat)}
                  </Text>
                </View>
              </View>

              <View style={styles.goalProgress}>
                {renderProgressBar(goal.currentAmount, goal.targetAmount, goal.type)}
                <View style={styles.goalStats}>
                  <Text style={styles.goalAmount}>
                    {formatCurrency(goal.currentAmount, settings.currency.code, settings.currency.symbol)} / {formatCurrency(goal.targetAmount, settings.currency.code, settings.currency.symbol)}
                  </Text>
                  <Text style={styles.goalPercentage}>{progress.toFixed(2)}%</Text>
                </View>
              </View>

              <View style={styles.goalFooter}>
                <View style={styles.monthlyContainer}>
                  <Text style={styles.monthlyLabel}>Monthly</Text>
                  <Text style={styles.monthlyAmount}>
                    {formatCurrency(goal.monthlyContribution, settings.currency.code, settings.currency.symbol)}
                  </Text>
                </View>
                <View style={styles.deadlineContainer}>
                  {goal.type === 'Debt' && goal.originalTargetAmount && (
                    <Text style={styles.originalDebtText}>
                      Original Debt: {formatCurrency(goal.originalTargetAmount, settings.currency.code, settings.currency.symbol)}
                    </Text>
                  )}
                  <Text style={styles.typeBalance}>
                    Balance: {formatCurrency(remainingBalance, settings.currency.code, settings.currency.symbol)}
                  </Text>
                  {goal.type === 'Debt' && goal.apr && (
                    <>
                      <Text style={styles.typeInterest}>
                        Interest: {formatCurrency(goal.targetAmount - (goal.originalTargetAmount || goal.targetAmount), settings.currency.code, settings.currency.symbol)}
                      </Text>
                      {goal.payments?.length > 0 && goal.apr && goal.originalTargetAmount && (
                        <Text style={styles.interestPaidText}>
                          Interest Paid: {formatCurrency((() => {
                            // Sort payments by date
                            const sortedPayments = [...goal.payments].sort((a, b) => 
                              new Date(a.date).getTime() - new Date(b.date).getTime()
                            );

                            let totalInterestPaid = 0;
                            let currentBalance = goal.originalTargetAmount;
                            let lastPaymentDate: Date | null = null;
                            let firstPaymentOfDay = true;

                            // Calculate daily interest rate
                            const dailyRate = goal.apr / 100 / 365;

                            sortedPayments.forEach(payment => {
                              const paymentDate = new Date(payment.date);
                              
                              // Check if this is a new day
                              if (lastPaymentDate && paymentDate.getTime() !== lastPaymentDate.getTime()) {
                                firstPaymentOfDay = true;
                              }

                              // If this is not the first payment ever, calculate interest
                              if (lastPaymentDate) {
                                const daysDiff = Math.floor(
                                  (paymentDate.getTime() - lastPaymentDate.getTime()) / (1000 * 60 * 60 * 24)
                                );
                                
                                // Add accumulated interest for days between payments
                                if (daysDiff > 0) {
                                  const interestAccumulated = currentBalance * dailyRate * daysDiff;
                                  currentBalance += interestAccumulated;
                                }
                              }

                              // Calculate interest portion for this payment
                              const dailyInterest = currentBalance * dailyRate;
                              const interestPortion = firstPaymentOfDay ? dailyInterest : 0;
                              totalInterestPaid += interestPortion;

                              // Update tracking variables
                              currentBalance = Math.max(0, currentBalance - payment.amount);
                              lastPaymentDate = paymentDate;
                              firstPaymentOfDay = false;
                            });

                            return totalInterestPaid;
                          })(), settings.currency.code, settings.currency.symbol)}
                        </Text>
                      )}
                    </>
                  )}
                </View>
              </View>
            </View>
          );
        })}
      </View>

      {/* Ad Banner */}
      <AdBanner />
    </>
  );

  const goalLimitModal = (
    <GoalLimitModal
      visible={goalLimitModalVisible}
      onClose={() => setGoalLimitModalVisible(false)}
      onUpgrade={() => {
        setGoalLimitModalVisible(false);
        navigation.navigate('Subscription');
      }}
      currentGoals={goals.length}
      maxGoals={subscription?.tier === 'standard' ? 10 : 3}
    />
  );

  if (isWeb) {
    const dashboardTitle = `${getTimeOfDay()}${getFirstName() ? `, ${getFirstName()}` : ''}`;

    return (
      <>
        <WebPage
          footer={quickActionsBar}
          fullHeight
          title={dashboardTitle}
          subtitle="Here's your financial overview"
        >
          <View
            style={[
              styles.container,
              styles.containerWeb,
              { backgroundColor: themeColors.background.paper },
            ]}
          >
            {dashboardScrollBody}
          </View>
        </WebPage>
        {goalLimitModal}
      </>
    );
  }

  return (
    <View style={styles.screenLayout}>
      <ScrollView
        style={[
          styles.container,
          { backgroundColor: themeColors.background.paper },
        ]}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator
      >
        {dashboardScrollBody}
      </ScrollView>
      {quickActionsBar}
      {goalLimitModal}
    </View>
  );
};

const useDashboardStyles = createThemedStyles((colors) => ({
  screenLayout: {
    flex: 1,
    backgroundColor: colors.background.paper,
  },
  container: {
    flex: 1,
    backgroundColor: colors.background.goals,
  },
  containerWeb: {
    backgroundColor: colors.background.paper,
  },
  scrollContent: {
    paddingBottom: 8,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: colors.primary.header,
    paddingVertical: 16,
    paddingHorizontal: 20,
    elevation: 4,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2,
    shadowRadius: 4,
  },
  brandContainer: {
    flex: 1,
  },
  brandTagline: {
    fontSize: 12,
    color: 'rgba(255, 255, 255, 0.9)',
    marginTop: 2,
  },
  settingsButton: {
    padding: 8,
  },
  welcomeSection: {
    padding: 20,
    backgroundColor: colors.background.paper,
    borderBottomWidth: 1,
    borderBottomColor: colors.grey[200],
  },
  greeting: {
    fontSize: 24,
    fontWeight: 'bold',
    color: colors.text.primary,
  },
  subtitle: {
    fontSize: 16,
    color: colors.text.secondary,
    marginTop: 4,
  },
  summaryContainer: {
    flexDirection: 'row',
    padding: 16,
    gap: 16,
  },
  summaryContainerWeb: {
    paddingHorizontal: 0,
    paddingVertical: 16,
  },
  summaryCard: {
    flex: 1,
    padding: 16,
    borderRadius: 12,
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  summaryLabel: {
    fontSize: 14,
    color: 'white',
    opacity: 0.9,
  },
  summaryValue: {
    fontSize: 24,
    fontWeight: 'bold',
    color: 'white',
    marginVertical: 4,
  },
  summarySubtext: {
    fontSize: 12,
    color: 'white',
    opacity: 0.9,
  },
  section: {
    paddingTop: 0,
    padding: 16,
  },
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: colors.text.primary,
  },
  sectionSubtitle: {
    fontSize: 14,
    color: colors.text.secondary,
  },
  goalCard: {
    backgroundColor: colors.surface,
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    width: '100%',
  },
  goalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  goalTitleContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    paddingRight: 16,
  },
  goalMetaContainer: {
    alignItems: 'flex-end',
    minWidth: 100,
  },
  goalIcon: {
    fontSize: 20,
    marginRight: 8,
  },
  goalName: {
    fontSize: 16,
    fontWeight: 'bold',
    color: colors.text.primary,
    flex: 1,
    flexShrink: 1,
  },
  priorityBadge: {
    paddingHorizontal: 12,
    paddingVertical: 4,
    borderRadius: 12,
    marginBottom: 8,
  },
  priorityText: {
    color: '#ffffff',
    fontSize: 12,
    fontWeight: 'bold',
    textAlign: 'center',
  },
  goalProgress: {
    marginBottom: 12,
  },
  progressBarContainer: {
    height: 8,
    backgroundColor: colors.grey[200],
    borderRadius: 4,
    overflow: 'hidden',
  },
  progressBar: {
    height: '100%',
    borderRadius: 4,
  },
  goalStats: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 8,
    flexWrap: 'wrap',
    gap: 8,
  },
  goalAmount: {
    fontSize: 14,
    color: colors.text.primary,
    flexShrink: 1,
    minWidth: 150,
  },
  goalPercentage: {
    fontSize: 14,
    fontWeight: 'bold',
    color: colors.primary.main,
    textAlign: 'right',
    minWidth: 60,
  },
  goalFooter: {
    flexDirection: 'column',
    gap: 8,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: colors.grey[200],
  },
  monthlyContainer: {
    width: '100%',
  },
  monthlyLabel: {
    fontSize: 12,
    color: colors.text.secondary,
  },
  monthlyAmount: {
    fontSize: 14,
    fontWeight: 'bold',
    color: colors.text.primary,
    marginTop: 2,
  },
  deadlineContainer: {
    width: '100%',
  },
  deadlineLabel: {
    fontSize: 12,
    color: colors.text.secondary,
    marginBottom: 4,
  },
  deadlineValue: {
    fontSize: 14,
    color: colors.text.primary,
    marginBottom: 4,
  },
  quickActions: {
    flexDirection: 'row',
    paddingHorizontal: 16,
    paddingTop: 12,
    paddingBottom: 12,
    gap: 16,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: colors.border,
    backgroundColor: colors.background.paper,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: -2 },
    shadowOpacity: 0.06,
    shadowRadius: 4,
    elevation: 8,
  },
  actionButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 12,
    backgroundColor: colors.surface,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: colors.primary.main,
    gap: 8,
  },
  actionText: {
    fontSize: 14,
    color: colors.primary.main,
    fontWeight: '600',
  },
  goalDeadline: {
    display: 'none',
  },
  deadlineText: {
    display: 'none',
  },
  typeSummaryContainer: {
    padding: 0,
    gap: 12,
  },
  typeSummaryCard: {
    width: '100%',
    padding: 16,
    borderRadius: 12,
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    backgroundColor: colors.surface,
  },
  typeHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 16,
  },
  typeIconContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    paddingRight: 16,
  },
  typeIcon: {
    fontSize: 24,
    marginRight: 8,
  },
  typeLabel: {
    fontSize: 16,
    fontWeight: 'bold',
    color: colors.text.primary,
    flex: 1,
  },
  typeMetaContainer: {
    alignItems: 'flex-end',
    minWidth: 120,
  },
  typeGoalCount: {
    fontSize: 13,
    color: colors.text.secondary,
    marginBottom: 6,
    textAlign: 'right',
    fontWeight: '500',
  },
  typeDeadline: {
    fontSize: 12,
    color: colors.text.secondary,
    textAlign: 'right',
  },
  typeAmount: {
    fontSize: 28,
    fontWeight: 'bold',
    marginBottom: 8,
  },
  typeProgressBar: {
    height: 8,
    backgroundColor: 'rgba(0, 0, 0, 0.1)',
    borderRadius: 2,
    marginBottom: 8,
    position: 'relative',
  },
  typeProgressFill: {
    height: '100%',
    borderRadius: 2,
  },
  typeProgressDetails: {
    marginTop: 24,
    marginBottom: 8,
    alignItems: 'flex-start',
  },
  typeProgress: {
    fontSize: 14,
    fontWeight: '600',
    color: '#000000',
    marginBottom: 4,
  },
  typeBalance: {
    fontSize: 14,
    color: colors.text.primary,
    marginTop: 2,
  },
  typeInterest: {
    fontSize: 14,
    color: colors.error.main,
    marginTop: 4,
  },
  originalDebtText: {
    fontSize: 14,
    color: colors.text.primary,
    marginBottom: 4,
  },
  interestPaidText: {
    fontSize: 14,
    color: colors.success.main,
    marginTop: 4,
  },
  progressText: {
    position: 'absolute',
    right: 0,
    top: 12,
    fontSize: 14,
    fontWeight: 'bold',
  },
  dashboardSearchFilterContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 0,
    paddingHorizontal: 0,
    gap: 12,
  },
  dashboardSearchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    backgroundColor: colors.grey[100],
    borderRadius: 8,
    paddingHorizontal: 14,
    paddingVertical: isWeb ? 12 : 10,
    minHeight: isWeb ? 48 : 44,
  },
  dashboardSearchIcon: {
    marginRight: 8,
  },
  dashboardSearchInput: {
    flex: 1,
    fontSize: 16,
    color: colors.text.primary,
    paddingVertical: isWeb ? 4 : 2,
    ...(isWeb ? { outlineStyle: 'none' as const } : {}),
  },
  dashboardClearButton: {
    padding: 8,
    borderRadius: 20,
  },
  dashboardFilterToggleButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: isWeb ? 12 : 10,
    minHeight: isWeb ? 48 : 44,
    borderRadius: 8,
    backgroundColor: colors.grey[100],
  },
  dashboardFilterToggleText: {
    marginRight: 8,
  },
  dashboardFilterOptionsContainer: {
    paddingVertical: 0,
    paddingHorizontal: 0,
  },
  dashboardFilterSection: {
    marginBottom: 16,
  },
  dashboardFilterSectionTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: colors.text.primary,
    marginBottom: 8,
  },
  dashboardFilterChipsContainer: {
    flexDirection: 'row',
    gap: 8,
  },
  dashboardFilterChip: {
    padding: 8,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: colors.button.primary,
  },
  dashboardFilterChipSelected: {
    backgroundColor: colors.button.primary,
  },
  dashboardFilterChipText: {
    fontSize: 14,
    color: colors.text.primary,
  },
  dashboardFilterChipTextSelected: {
    fontWeight: 'bold',
  },
  dashboardFilterChipIcon: {
    marginRight: 8,
  },
  dashboardClearFiltersButton: {
    padding: 8,
    alignItems: 'center',
  },
  dashboardClearFiltersButtonText: {
    fontSize: 14,
    color: colors.primary.main,
    fontWeight: 'bold',
  },
}));

export default DashboardScreen; 